import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useCurrentStudy } from "@/context/CurrentStudyContext";
import { Loader2, Sparkles, Quote, CheckCircle2, ChevronDown, ChevronRight, Bot, RefreshCw, AlertTriangle, FileDown } from "lucide-react";
import { toast } from "sonner";
import { downloadXlsx } from "@/lib/download";

const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 });
const LEVEL_STYLES = {
  P1: "bg-rose-50 text-rose-700 border-rose-200",
  P2: "bg-amber-50 text-amber-700 border-amber-200",
  P3: "bg-sky-50 text-sky-700 border-sky-200",
  P4: "bg-slate-100 text-slate-600 border-slate-200",
};
const PLAN_STEPS = [["d30", "0-30 días"], ["d60", "31-60 días"], ["d90", "61-90 días"]];
const COL_LABELS = {
  gh: "Molécula (G.H.)", state_label: "Semáforo", marca_units: "Uds marca",
  marca_euros: "€ marca", generic_share_pct: "% genérico", units_year: "Uds/año",
  order_units: "Pedir (10 %)", lab: "Laboratorio", molecules: "Moléculas",
  euros: "€ genérico", has_conditions: "¿Con condición?", gh_pvl: "PVL",
  marca_units_month: "Uds marca/mes", extra_benefit_eur: "Beneficio extra €",
  team_eur_per_change: "Al equipo por cambio",
};

const Metric = ({ m }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3">
    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{m.label}</div>
    <div className="font-mono-num font-bold text-lg mt-0.5 text-slate-900">
      {fmtNum(m.value)}{m.suffix || ""}
    </div>
    {m.delta_pp !== undefined && (
      <div className={`text-[11px] font-bold ${m.delta_pp >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
        {m.delta_pp >= 0 ? "+" : ""}{m.delta_pp} pp vs guía ({m.target}%)
      </div>
    )}
    {m.note && <div className="text-[11px] text-slate-500 mt-0.5">{m.note}</div>}
  </div>
);

const Block = ({ b }) => {
  const [open, setOpen] = useState(b.priority <= 2);
  const cols = b.items_columns || [];
  return (
    <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`reco-block-${b.id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start gap-3 text-left px-5 py-4 hover:bg-slate-50"
        data-testid={`reco-toggle-${b.id}`}
      >
        <span className="text-2xl leading-none mt-0.5">{b.icon}</span>
        <span className="flex-1">
          <span className="block font-bold text-slate-900">{b.title}</span>
          <span className="block text-[11px] text-slate-500 mt-0.5">{b.guide_ref}</span>
        </span>
        {open ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">{b.why}</p>

          {!!(b.metrics || []).length && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {b.metrics.map((m) => <Metric key={m.label} m={m} />)}
            </div>
          )}

          <ul className="space-y-1.5">
            {b.actions.map((a) => (
              <li key={a} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                <span>{a}</span>
              </li>
            ))}
          </ul>

          {!!(b.quotes || []).length && (
            <div className="space-y-1.5">
              {b.quotes.map((q) => (
                <div key={q} className="flex items-center gap-2 text-sm italic text-slate-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <Quote className="h-3.5 w-3.5 text-amber-600 shrink-0" /> {q}
                </div>
              ))}
            </div>
          )}

          {!!(b.items || []).length && (
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-500 uppercase text-[10px] tracking-wider">
                  <tr>{cols.map((c) => <th key={c} className={`p-2 ${c === "gh" || c === "lab" ? "text-left" : "text-right"}`}>{COL_LABELS[c] || c}</th>)}</tr>
                </thead>
                <tbody>
                  {b.items.map((it, i) => (
                    <tr key={`${it.gh || it.lab}-${i}`} className="border-t border-slate-100">
                      {cols.map((c) => (
                        <td key={c} className={`p-2 ${c === "gh" || c === "lab" ? "text-left font-semibold text-slate-800 max-w-[340px] truncate" : "text-right font-mono-num"}`}
                            title={typeof it[c] === "string" ? it[c] : undefined}>
                          {typeof it[c] === "boolean"
                            ? (it[c] ? "Sí" : "No")
                            : typeof it[c] === "number"
                              ? fmtNum(it[c]) + (c.includes("euros") || c.includes("eur") || c === "gh_pvl" ? " €" : c.includes("pct") ? " %" : "")
                              : (it[c] ?? "—")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default function RecommendationsPage() {
  const { current, loading: studyLoading } = useCurrentStudy();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const dispId = current?.id;

  useEffect(() => {
    if (studyLoading) return;
    if (!dispId) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    api.get(`/dispensations/${dispId}/recommendations`)
      .then(({ data }) => { if (alive) { setData(data); setAi(data.ai || null); } })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dispId, studyLoading]);

  const k = useMemo(() => data?.kpis || {}, [data]);

  // Informe único por farmacia: Claude analiza estos datos con la guía.
  const pollAi = async () => {
    for (let i = 0; i < 40; i++) {
      await new Promise((r) => setTimeout(r, 4000));
      try {
        const { data: d } = await api.get(`/dispensations/${dispId}/recommendations`);
        setAi(d.ai || null);
        if (d.ai?.status === "ready") { toast.success("Informe personalizado listo"); return; }
        if (d.ai?.status === "error") { toast.error("La IA no pudo completar el informe"); return; }
      } catch (e) { /* seguimos intentando */ }
    }
  };

  const generateAi = async () => {
    setAiBusy(true);
    setAi({ status: "generating" });
    try {
      await api.post(`/dispensations/${dispId}/recommendations/ai`);
      await pollAi();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo lanzar el análisis");
      setAi(null);
    } finally {
      setAiBusy(false);
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadXlsx(`/dispensations/${dispId}/recommendations/pdf`, "recomendaciones.pdf");
    } finally {
      setPdfBusy(false);
    }
  };

  if (loading) {
    return <div className="p-8 lg:p-12 text-slate-500" data-testid="reco-loading">Preparando tus recomendaciones…</div>;
  }
  if (!data) {
    return (
      <div className="p-8 lg:p-12 max-w-3xl" data-testid="reco-empty">
        <h1 className="font-display text-3xl font-bold text-slate-900">Recomendaciones</h1>
        <p className="text-slate-600 mt-3">Sube un estudio de dispensación y aquí tendrás el plan de acción.</p>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-[1400px]" data-testid="recommendations-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Plan de acción</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-emerald-600" /> Recomendaciones
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl text-sm">
            Plan de mejora para <strong>{data.study?.label}</strong> siguiendo la {data.guide?.title}.
            Las acciones están calculadas con los datos de este estudio, ordenadas por prioridad.
          </p>
        </div>
        <button
          onClick={downloadPdf}
          disabled={pdfBusy}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-xs font-bold px-4 py-2.5 rounded-lg"
          data-testid="reco-download-pdf"
        >
          {pdfBusy
            ? <><Loader2 className="h-4 w-4 animate-spin" /> Preparando PDF…</>
            : <><FileDown className="h-4 w-4" /> Descargar dossier (PDF)</>}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mt-6" data-testid="reco-kpis">
        {[
          { l: "% genérico uds", v: `${k.pct_units}%`, d: k.delta_units_pp, t: data.guide?.avg_units_pct },
          { l: "% genérico €", v: `${k.pct_euros}%`, d: k.delta_euros_pp, t: data.guide?.avg_euros_pct },
          { l: "Moléculas en rojo", v: fmtNum(k.red_molecules), s: `${fmtNum(k.red_euros)} € de marca` },
          { l: "Labs de genérico", v: fmtNum(k.generic_labs), s: `${fmtNum(k.multi_lab_molecules)} moléculas con varios` },
          { l: "Candidatas a SPD", v: fmtNum(k.spd_candidates), s: "PVL ≥ 20 €" },
        ].map((c) => (
          <div key={c.l} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{c.l}</div>
            <div className="font-mono-num font-bold text-xl mt-0.5 text-slate-900">{c.v}</div>
            {c.d !== undefined ? (
              <div className={`text-[11px] font-bold ${c.d >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {c.d >= 0 ? "+" : ""}{c.d} pp vs media {c.t}%
              </div>
            ) : <div className="text-[11px] text-slate-500">{c.s}</div>}
          </div>
        ))}
      </div>

      <section className="mt-6 rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-5" data-testid="reco-ai-block">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-bold text-slate-900 flex items-center gap-2">
              <Bot className="h-5 w-5 text-indigo-600" />
              Informe personalizado de esta farmacia
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Claude analiza los datos de <strong>{data.study?.label}</strong> con los criterios de la guía y escribe
              un plan propio para esta farmacia.
            </p>
          </div>
          <button
            onClick={generateAi}
            disabled={aiBusy || ai?.status === "generating"}
            className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-xs font-bold px-4 py-2.5 rounded-lg"
            data-testid="reco-ai-generate"
          >
            {aiBusy || ai?.status === "generating"
              ? <><Loader2 className="h-4 w-4 animate-spin" /> Analizando…</>
              : <><RefreshCw className="h-4 w-4" /> {ai?.status === "ready" ? "Volver a analizar" : "Generar informe con IA"}</>}
          </button>
        </div>

        {ai?.status === "generating" && (
          <p className="text-sm text-slate-600 mt-4" data-testid="reco-ai-generating">
            Estoy leyendo vuestros números y cruzándolos con la guía. Tarda entre 30 y 60 segundos.
          </p>
        )}
        {ai?.status === "error" && (
          <p className="text-sm text-rose-700 mt-4 flex items-start gap-2" data-testid="reco-ai-error">
            <AlertTriangle className="h-4 w-4 mt-0.5" /> No se pudo generar el informe. Vuelve a intentarlo.
          </p>
        )}

        {ai?.status === "ready" && (
          <div className="mt-4 space-y-4" data-testid="reco-ai-report">
            <p className="text-lg font-bold text-slate-900 leading-snug">{ai.headline}</p>
            <p className="text-sm text-slate-700 leading-relaxed">{ai.diagnosis}</p>

            <div className="space-y-3">
              {(ai.priorities || []).map((p, i) => (
                <div key={p.title} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`reco-ai-priority-${i}`}>
                  <div className="flex items-start gap-2">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-bold text-slate-900 text-sm">{p.title}</div>
                        {p.level && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${LEVEL_STYLES[(p.level || "").toUpperCase().slice(0, 2)] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                            {p.level}
                          </span>
                        )}
                        {p.horizon && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">
                            {p.horizon}
                          </span>
                        )}
                      </div>
                      {p.why && <p className="text-sm text-slate-600 mt-1">{p.why}</p>}
                      {p.cause && (
                        <p className="text-xs text-slate-500 mt-1.5">
                          <span className="font-bold uppercase tracking-wide text-[10px] text-slate-400">Causa · </span>
                          {p.cause}
                        </p>
                      )}
                      {!!(p.how || []).length && (
                        <ul className="mt-2 space-y-1">
                          {p.how.map((h) => (
                            <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                              <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> {h}
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {p.impact && (
                          <div className="text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1">
                            Impacto: {p.impact}
                          </div>
                        )}
                        {p.kpi && (
                          <div className="text-xs font-bold text-sky-700 bg-sky-50 border border-sky-200 rounded-md px-2 py-1">
                            KPI: {p.kpi}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {ai.team_message && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Para leer al equipo</div>
                <p className="text-sm text-slate-800 mt-1 leading-relaxed">{ai.team_message}</p>
              </div>
            )}

            {!!(ai.desk || []).length && (
              <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="reco-ai-desk">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">En el mostrador, según el paciente</div>
                <div className="mt-2 space-y-2">
                  {ai.desk.map((d) => (
                    <div key={d.situation} className="text-sm">
                      <span className="font-bold text-slate-900">{d.situation}: </span>
                      <span className="text-slate-700">{d.advice}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!!PLAN_STEPS.filter(([key]) => (ai.plan?.[key] || []).length).length && (
              <div className="grid gap-3 sm:grid-cols-3" data-testid="reco-ai-plan">
                {PLAN_STEPS.map(([key, label]) => (
                  !!(ai.plan?.[key] || []).length && (
                    <div key={key} className="rounded-xl border border-slate-200 bg-white p-4">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-600">{label}</div>
                      <ul className="mt-2 space-y-1">
                        {ai.plan[key].map((a) => (
                          <li key={a} className="flex items-start gap-2 text-sm text-slate-700">
                            <ChevronRight className="h-4 w-4 text-indigo-500 mt-0.5 shrink-0" /> {a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                ))}
              </div>
            )}

            {ai.quality_check && (
              <div className="rounded-xl border border-sky-200 bg-sky-50 p-4" data-testid="reco-ai-control">
                <div className="text-[10px] uppercase tracking-widest font-bold text-sky-700">Control de calidad del dato</div>
                <p className="text-sm text-slate-800 mt-1 leading-relaxed">{ai.quality_check}</p>
              </div>
            )}

            {!!(ai.next_visit || []).length && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Qué revisar en la próxima visita</div>
                <ul className="mt-1.5 space-y-1">
                  {ai.next_visit.map((n) => (
                    <li key={n} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 text-sky-600 mt-0.5 shrink-0" /> {n}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-[11px] text-slate-400">
              Generado con {ai.model} el {new Date(ai.generated_at).toLocaleString("es-ES")} · revisa siempre antes de compartirlo
            </p>
          </div>
        )}
      </section>

      <div className="space-y-4 mt-6">
        {data.blocks.map((b) => <Block key={b.id} b={b} />)}
      </div>
    </div>
  );
}
